import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useProformaInvoices } from "@/api/hooks";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { ErrorState, LoadingState } from "@/components/ui/States";
import { formatMoney } from "@/domain/money";
export function InvoiceSourcePicker({
  onPick,
  onClose,
  keepPiId,
}: {
  onPick: (id: string) => void;
  onClose: () => void;
  /** PI đang gắn với hóa đơn — giữ trong danh sách để chọn lại lấy giá mới. */
  keepPiId?: string;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const result = useProformaInvoices({
    q: q.trim() || undefined,
    page,
    pageSize: 25,
    invoiceAvailable: "true",
    keepPiId,
  });
  return (
    <Modal
      title={t("invoices.selectPi")}
      closeLabel={t("common.close")}
      onClose={onClose}
    >
      <div className="space-y-3 p-4">
        <Input
          aria-label={t("invoices.searchPiHint")}
          placeholder={t("invoices.searchPiHint")}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
        />
        {result.isLoading ? (
          <LoadingState />
        ) : result.error ? (
          <ErrorState
            message={t("pi.errorList")}
            onRetry={() => void result.refetch()}
          />
        ) : (
          <>
            <ul className="max-h-[50dvh] divide-y divide-line overflow-auto">
              {result.data?.data.map((pi) => (
                <li key={pi.id}>
                  <button
                    disabled={result.isFetching}
                    onClick={() => onPick(pi.id)}
                    className="flex w-full flex-wrap items-center gap-3 rounded-sm p-3 text-left hover:bg-sunken"
                  >
                    <span className="font-mono text-sm text-brand">
                      {pi.piNo}
                    </span>
                    <span className="min-w-40 flex-1 text-xs">
                      {pi.billToName}
                      <span className="mt-1 block font-mono text-ink-faint">
                        {pi.customerPoNo}
                      </span>
                    </span>
                    <span className="font-mono text-xs">
                      {formatMoney(pi.totalAmount, pi.currency)} {pi.currency}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            {result.data?.total === 0 && (
              <p className="text-sm text-ink-soft">
                {t("invoiceWorkspace.noSources")}
              </p>
            )}
            <div className="flex items-center justify-between gap-3">
              <Button
                variant="secondary"
                disabled={page <= 1 || result.isFetching}
                onClick={() => setPage((p) => p - 1)}
              >
                {t("pagination.previous")}
              </Button>
              <span className="font-mono text-xs">
                {page} /{" "}
                {Math.max(1, Math.ceil((result.data?.total ?? 0) / 25))}
              </span>
              <Button
                variant="secondary"
                disabled={
                  page * 25 >= (result.data?.total ?? 0) || result.isFetching
                }
                onClick={() => setPage((p) => p + 1)}
              >
                {t("pagination.next")}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
